import React, { useState, useEffect } from 'react';
import { AmountYield, ElectrodeType } from '@twinsketch/topika-model';
import 'src/styles/workOrder/work-order.css';
import 'src/styles/workOrder/mixing-table.css';
import { FormulationToMixingHelper } from '../../helper';
import { FiExternalLink, FiEdit2, FiPlus, FiCheck, FiX } from 'react-icons/fi';

interface AmountYieldTableProps {
  materialsNeeded: AmountYield[] | null | undefined;
  formulation: FormulationToMixingHelper | null;
  onUpdate: (data: AmountYield[]) => Promise<void>;
  readonly?: boolean;
}

const formatNumber = (value: number | null | undefined) => {
  return typeof value === 'number' ? value.toFixed(2) : 'N/A';
};

type AmountYieldFormData = {
  id: string;
  materialName: string;
  net: number | undefined;
  min: number | undefined;
};

const AmountYieldsTable: React.FC<AmountYieldTableProps> = ({
  materialsNeeded,
  formulation,
  onUpdate,
  readonly = false,
}) => {
  const [isEditing, setIsEditing] = useState(false);
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState<AmountYieldFormData[]>(
    createInitialFormData(materialsNeeded, formulation)
  );

  useEffect(() => {
    if (!isEditing) {
      setFormData(createInitialFormData(materialsNeeded, formulation));
    }
  }, [materialsNeeded, formulation, isEditing]);

  const handleChange = (index: number, field: keyof AmountYieldFormData, value: string) => {
    setFormData(prev => {
      const updated = [...prev];
      if (field === 'materialName' || field === 'id') {
        updated[index] = { ...updated[index], [field]: value };
      } else {
        const numValue = value === '' ? undefined : Number(value);
        updated[index] = { ...updated[index], [field]: numValue };
      }
      return updated;
    });
  };

  const handleAddRow = () => {
    setFormData(prev => [
      ...prev,
      {
        id: `new-${Date.now()}`,
        materialName: '',
        net: undefined,
        min: undefined,
      },
    ]);
  };

  const handleRemoveRow = (index: number) => {
    setFormData(prev => prev.filter((_, i) => i !== index));
  };

  const handleSave = async () => {
    setLoading(true);
    try {
      // Filter out incomplete rows and ensure required fields are present
      const validData = formData.filter(
        item =>
          item.materialName &&
          item.materialName.trim() !== '' &&
          item.net !== undefined &&
          item.min !== undefined
      );
      await onUpdate(validData);
      setIsEditing(false);
    } catch (error) {
      console.error('Failed to save material amounts:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    setFormData(createInitialFormData(materialsNeeded, formulation));
    setIsEditing(false);
  };

  const canEdit = !readonly && !isEditing;

  return (
    <>
      <div className="formulation-title-row">
        <span className="formulation-title">Material Amount</span>

        {canEdit && !isEditing && (
          <button
            className="formulation-link"
            onClick={() => setIsEditing(true)}
            title="Edit"
            style={{
              border: 'none',
              background: 'none',
              cursor: 'pointer',
              padding: '4px',
            }}
          >
            <FiEdit2 size={16} color="#666" />
          </button>
        )}

        {isEditing && (
          <div className="action-buttons">
            <button
              className="formulation-link"
              onClick={handleSave}
              disabled={loading}
              title="Save"
              style={{
                border: 'none',
                background: 'none',
                cursor: 'pointer',
                padding: '4px',
                marginRight: '4px',
              }}
            >
              <FiCheck size={16} color="#28a745" />
            </button>
            <button
              className="formulation-link"
              onClick={handleCancel}
              disabled={loading}
              title="Cancel"
              style={{
                border: 'none',
                background: 'none',
                cursor: 'pointer',
                padding: '4px',
              }}
            >
              <FiX size={16} color="#dc3545" />
            </button>
            <button
              className="formulation-link"
              onClick={handleAddRow}
              disabled={loading}
              style={{
                marginLeft: '8px',
                background: 'none',
                cursor: 'pointer',
                fontSize: '12px',
                color: '#666',
              }}
            >
              <FiPlus size={12} style={{ marginRight: '4px', color: '#666' }} />
              <span>Add Row</span>
            </button>
          </div>
        )}
      </div>

      <table className="mixing-table">
        <thead>
          <tr>
            <th>Material</th>
            <th>Net (kg)</th>
            <th>수율적용 (kg)</th>
            {isEditing && <th style={{ width: '60px' }}>Action</th>}
          </tr>
        </thead>
        <tbody>
          {formData.length === 0 ? (
            <tr>
              <td colSpan={isEditing ? 4 : 3} style={{ textAlign: 'center' }}>
                No materials added
              </td>
            </tr>
          ) : (
            formData.map((material, index) => (
              <tr key={material.id}>
                <td>
                  {isEditing ? (
                    <input
                      type="text"
                      className="table-input"
                      placeholder="Material name"
                      value={material.materialName ?? ''}
                      onChange={e => handleChange(index, 'materialName', e.target.value)}
                    />
                  ) : (
                    material.materialName || 'N/A'
                  )}
                </td>
                <td>
                  {isEditing ? (
                    <input
                      type="number"
                      className="table-input-small"
                      placeholder="0.00"
                      value={material.net ?? ''}
                      onChange={e => handleChange(index, 'net', e.target.value)}
                      step="0.01"
                      min="0"
                    />
                  ) : (
                    formatNumber(material.net)
                  )}
                </td>
                <td>
                  {isEditing ? (
                    <input
                      type="number"
                      className="table-input-small"
                      placeholder="0.00"
                      value={material.min ?? ''}
                      onChange={e => handleChange(index, 'min', e.target.value)}
                      step="0.01"
                      min="0"
                    />
                  ) : (
                    formatNumber(material.min)
                  )}
                </td>
                {isEditing && (
                  <td>
                    <button
                      className="delete-button"
                      onClick={() => handleRemoveRow(index)}
                      title="Remove row"
                      disabled={loading}
                    >
                      🗑️
                    </button>
                  </td>
                )}
              </tr>
            ))
          )}
        </tbody>
      </table>
    </>
  );
};

// ========== Helper Functions ==========
function createInitialFormData(
  materialsNeeded: AmountYield[] | null | undefined,
  formulation: FormulationToMixingHelper | null
): AmountYieldFormData[] {
  // If materialsNeeded exists and has data, use it
  if (materialsNeeded && materialsNeeded.length > 0) {
    return materialsNeeded.map((material, index) => ({
      id: `existing-${index}`,
      materialName: material.materialName,
      net: material.net,
      min: material.min,
    }));
  }

  // If materialsNeeded is empty but formulation exists, populate from formulation
  if (formulation?.formulation?.elements && formulation.formulation.elements.length > 0) {
    const formulationRows = formulation.formulation.elements.map((element, index) => ({
      id: `formulation-${element.materialId}-${index}`,
      materialName: element.material.materialName,
      net: undefined,
      min: undefined,
    }));

    // Check formulation type
    const formulationType = formulation?.formulation?.type;

    if (formulationType === ElectrodeType.CATHODE) {
      // Add NMP at the top and 20um Al Foil at the bottom
      return [
        {
          id: 'default-nmp',
          materialName: 'NMP',
          net: undefined,
          min: undefined,
        },
        ...formulationRows,
        {
          id: 'default-foil',
          materialName: '20um Al Foil',
          net: undefined,
          min: undefined,
        },
      ];
    } else if (formulationType === ElectrodeType.ANODE) {
      // Add DIW at the top and 10um Cu Foil at the bottom
      return [
        {
          id: 'default-diw',
          materialName: 'DIW',
          net: undefined,
          min: undefined,
        },
        ...formulationRows,
        {
          id: 'default-foil',
          materialName: '10um Cu Foil',
          net: undefined,
          min: undefined,
        },
      ];
    } else {
      // For other types or no type, just return formulation rows
      return formulationRows;
    }
  }

  // If both are empty, return empty array
  return [];
}

export default AmountYieldsTable;
